import {
  type MouseEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { VaultMediaType } from '@api'
import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import DollarReveiveIcon from '@/assets/icons/icon-dollar-receive.svg'
import EmailUploadIcon from '@/assets/icons/icon-email-upload.svg'
import EyeIcon from '@/assets/icons/icon-eye.svg'
import FreeIcon from '@/assets/icons/icon-free.svg'
import InboxUpIcon from '@/assets/icons/icon-inbox-up.svg'
import IconMore from '@/assets/icons/icon-menu-more.svg'
import SettingsIcon from '@/assets/icons/icon-settings.svg'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import { $postedMedia, $sentMedia } from '@/models/chattingUI/media.ts'
import { openDialog } from '@/models/dialogs.ts'
import { $isMediaCloningEnabled } from '@/models/features.ts'
import { $hasEditVaultPermission } from '@/models/permissions.ts'
import { $selectedMediaIds, toggleSelectedMedia } from '@/models/vault/medias'
import {
  $cloneToOriginalRegistry,
  $originalToCloneRegistry,
} from '@/models/vault/medias/clonned.ts'
import { $lastSelectedMediaId } from '@/models/vault/medias/selection.ts'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import type {
  StreamableVaultMedia,
  UploadedMediaAction,
} from '@/modules/vault/types.ts'
import { OverlayIconsTypes } from '@/modules/vault/types.ts'
import { VaultMediaItemFooter } from '@/modules/vault/ui/media/vault-media-item-footer.tsx'
import { getOverlaySettings } from '@/modules/vault/utils.ts'
import { classNames, stopEventPropagation } from '@/utils/helpers.ts'

import CloneIcon from '../../../chatting/icons/icon-message-new.svg'
import { VaultMediaPreview } from './vault-media-preview'

const hoverAnimationStyle =
  "relative rounded-full bg-[rgba(0,0,0,0.1)] after:pointer-events-none after:absolute after:left-0 after:top-0 after:h-full after:w-full after:scale-0 after:rounded-full after:bg-white/20 after:transition-transform after:ease-linear after:content-[''] hover:after:scale-100"

interface Props {
  item: StreamableVaultMedia
  showSettings?: boolean
  showBackground?: boolean
  className?: string
  onClear?: (mediaItem: StreamableVaultMedia) => void
  additionalActions?: UploadedMediaAction[]
  onClone?: (mediaItem: StreamableVaultMedia) => void
  onMediaPreview: (mediaItem: number) => void
  selectionEnabled?: boolean
}

const OverlayIcons = {
  [OverlayIconsTypes.posted]: <InboxUpIcon />,
  [OverlayIconsTypes.bought]: <DollarReveiveIcon />,
  [OverlayIconsTypes.sentFree]: <FreeIcon />,
  [OverlayIconsTypes.sent]: <EmailUploadIcon />,
}

export const VaultMediaItem = memo(
  ({
    item,
    className,
    showSettings = true,
    showBackground,
    selectionEnabled,
    additionalActions,
    onClear,
    onClone,
    onMediaPreview,
  }: Props) => {
    const isMediaCloningEnabled = useUnit($isMediaCloningEnabled)
    const hasEditVaultPermission = useUnit($hasEditVaultPermission)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const selectedMediaIds = useUnit($selectedMediaIds)
    const cloneToOriginalRegistry = useUnit($cloneToOriginalRegistry)
    const originalToCloneRegistry = useUnit($originalToCloneRegistry)
    const lastSelectedMediaId = useUnit($lastSelectedMediaId)
    const sentMedia = useUnit($sentMedia)
    const postedMedia = useUnit($postedMedia)

    const cloneIds = useMemo(() => {
      const ids: number[] = []

      const originalId = cloneToOriginalRegistry.get(item.id)

      if (originalId) {
        ids.push(originalId)
      }

      const cloneIds = originalToCloneRegistry.get(originalId ?? item.id)

      if (cloneIds) {
        ids.push(...cloneIds.filter((id) => id !== item.id))
      }

      return ids
    }, [item.id, cloneToOriginalRegistry, originalToCloneRegistry])

    const messagesOfMedia = useMemo(
      () =>
        sentMedia?.filter((s) =>
          s.media?.some((m) => m.id === item.id || cloneIds.includes(m.id)),
        ),
      [item.id, sentMedia, cloneIds],
    )
    const posted = useMemo(
      () =>
        postedMedia?.some(
          (postedId) => postedId === item.id || cloneIds.includes(postedId),
        ),
      [item.id, postedMedia, cloneIds],
    )

    const purchased = useMemo(
      () =>
        messagesOfMedia?.some(
          (m) => !m.isFree && m.canPurchaseReason !== 'free' && m.isOpened,
        ),
      [messagesOfMedia],
    )

    const isFree = useMemo(
      () => messagesOfMedia?.some((m) => m.isFree),
      [messagesOfMedia],
    )

    const overlaySettings = useMemo(
      () =>
        getOverlaySettings(
          !!posted,
          !!purchased,
          !!isFree,
          !!messagesOfMedia?.length,
        ),
      [isFree, messagesOfMedia?.length, posted, purchased],
    )

    const selectionIndex = selectedMediaIds.findIndex((i) => i === item.id)

    const date = useMemo(
      () => (item.createdAt ? new Date(item.createdAt) : null),
      [item.createdAt],
    )
    const isSelected = selectedMediaIds.includes(item.id)

    const handlePreviewClick = useCallback(
      (event: MouseEvent<HTMLOrSVGElement>) => {
        event.stopPropagation()
        onMediaPreview(item.id)
      },
      [item.id, onMediaPreview],
    )

    const handleCheckClick = useCallback(
      (event: MouseEvent<HTMLOrSVGElement>) => {
        event.stopPropagation()

        toggleSelectedMedia(item)
      },
      [item],
    )

    const handleSettingsClick = useCallback(
      (event: MouseEvent<HTMLOrSVGElement>) => {
        event.stopPropagation()
        openDialog({
          type: DIALOG_TYPE.MEDIA_SETTINGS,
          item,
        })
      },
      [item],
    )

    const handleClearClick = useCallback(
      (event: MouseEvent) => {
        if (onClear) {
          event.stopPropagation()
          onClear(item)
        }
      },
      [item, onClear],
    )

    const handleCloneClick = useCallback(
      (event: MouseEvent<HTMLOrSVGElement>) => {
        event.stopPropagation()
        onClone?.(item)
      },
      [item, onClone],
    )
    const container = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
      // if we previously select this media, scroll to it
      if (lastSelectedMediaId === item.id) {
        container.current?.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        })
      }
      // `lastSelectedMediaId` ignored intentionally
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id])

    return (
      <div
        ref={container}
        data-mediaid={item.id}
        data-testid="vault-media-item"
        onClick={selectionEnabled ? handleCheckClick : handlePreviewClick}
        className={classNames(
          'dark:bg-secondary-600 relative w-full cursor-pointer overflow-hidden rounded bg-white duration-200',
          className,
          {
            'dark:bg-[rgba(0,0,0,0.25)]': isSelected,
          },
        )}
      >
        <div className="relative left-0 top-0 flex aspect-square w-full items-center justify-center overflow-hidden rounded-md text-black">
          <div className="absolute right-2 top-2 z-10 flex items-center justify-center text-xs text-white">
            {isMediaCloningEnabled &&
              item.type !== VaultMediaType.AUDIO &&
              onClone && (
                <IconButton
                  icon={CloneIcon}
                  tooltip="Clone"
                  tooltipOptions={{ side: 'bottom' }}
                  onClick={handleCloneClick}
                  iconClassName="h-5 w-5 text-white"
                  className={classNames('mr-1 p-0.5', hoverAnimationStyle)}
                />
              )}
            {selectionEnabled && showSettings && hasEditVaultPermission && (
              <IconButton
                icon={SettingsIcon}
                tooltip="Settings"
                tooltipOptions={{ side: 'bottom' }}
                onClick={handleSettingsClick}
                iconClassName="h-4 w-4 text-white"
                className={classNames('mr-1 p-1', hoverAnimationStyle)}
              />
            )}
            {selectionEnabled && (
              <div className="flex items-center justify-center text-xs text-white">
                {selectionIndex >= 0 ? (
                  <div
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={0}
                    className="bg-primary-500 flex h-6 w-6 cursor-pointer select-none items-center justify-center rounded-full"
                  >
                    {selectionIndex + 1}
                  </div>
                ) : (
                  <Tooltip
                    content="Select"
                    side="bottom"
                    contentClassName="min-w-max text-title"
                    asChild
                  >
                    <div
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={0}
                      data-testid="vault-media-item-checkbox"
                      className={classNames('h-6 w-6 p-1', hoverAnimationStyle)}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="border-white"
                      />
                    </div>
                  </Tooltip>
                )}
              </div>
            )}

            {!!additionalActions?.length && (
              <div
                className="flex cursor-pointer items-center justify-center text-xs text-white"
                onClick={stopEventPropagation}
              >
                <DropdownMenu
                  open={isMenuOpen}
                  onOpenChange={setIsMenuOpen}
                >
                  <DropdownMenuTrigger className="cursor-pointer">
                    <IconMore className="mr-1 h-6 w-6 rounded-full bg-[rgba(0,0,0,0.4)] p-1 hover:bg-[rgba(0,0,0,0.5)]" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="p-1 z-[100]"
                    forceMount
                  >
                    {additionalActions.map((action) => (
                      <DropdownMenuItem
                        key={action.text}
                        onClick={action.onClick}
                      >
                        <span>{action.text}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {onClear && (
              <div
                onClick={handleClearClick}
                className="flex cursor-pointer items-center justify-center text-xs text-white"
              >
                <div className="rounded-full bg-[rgba(0,0,0,0.4)] p-1 hover:bg-[rgba(0,0,0,0.5)]">
                  <CloseIcon className="text-white" />
                </div>
              </div>
            )}
          </div>

          {selectionEnabled && (
            <div className="absolute z-10 bottom-2 left-2">
              <IconButton
                icon={EyeIcon}
                tooltip="Preview"
                tooltipOptions={{ side: 'bottom' }}
                onClick={handlePreviewClick}
                iconClassName="h-4 w-4 text-white"
                className={classNames(
                  'mr-1 p-1 ',
                  hoverAnimationStyle,
                  'bg-[rgba(0,0,0,0.3)]',
                )}
              />
            </div>
          )}

          {overlaySettings && (
            <>
              <div
                className="absolute left-0 top-0 z-[5] h-full w-full rounded-md opacity-40 pointer-events-none"
                style={{ background: overlaySettings.bgColor }}
              />

              {overlaySettings.tagColor && (
                <div
                  style={{ background: overlaySettings.tagColor }}
                  className="absolute left-1.5 top-1.5 z-10 flex items-center gap-1 rounded-xl px-2 py-1 text-sm text-white pointer-events-none"
                >
                  {overlaySettings.text}{' '}
                  {OverlayIcons[overlaySettings.iconType]}
                </div>
              )}
            </>
          )}

          {!selectionEnabled && item.isPreview && (
            <div className="absolute bottom-1.5 z-[5] rounded-2xl bg-[#00000055] px-2 py-1 text-xs font-medium text-white group-hover:pointer-events-none group-hover:opacity-0">
              Free Preview
            </div>
          )}

          <VaultMediaPreview
            className="h-full w-full rounded-md"
            files={item.files}
            type={item.type}
            onPlayClick={handlePreviewClick}
          />
        </div>
        <div className="h-[58px] w-full">
          <VaultMediaItemFooter
            label={item.mediaAttributes?.label}
            date={date}
            item={item}
            price={item.mediaAttributes?.price}
            showBackground={showBackground}
          />
        </div>
      </div>
    )
  },
)

VaultMediaItem.displayName = 'VaultMediaItem'
